import React from 'react';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { MenuItemProps } from '@europrocurement/flexy-components/components/molecules/MenuItem';
import { paddingContent } from '@europrocurement/flexy-components/assets';

export interface MobileBottomNavigationProps {
    items: MenuItemProps[];
    activeTab: string;
    onTabChange: (identifier: string) => void;
}

/**
 * Navigation en bas pour mobile
 * Affiche uniquement les items cliquables sous forme d'icônes
 */
export const MobileBottomNavigation: React.FunctionComponent<MobileBottomNavigationProps> =
    function ({ items, activeTab, onTabChange }) {
        const theme = useTheme();

        if (items.length === 0) {
            return null;
        }

        return (
            <Box
                sx={{
                    borderTop: `1px solid ${theme.palette.grey[300]}`,
                    backgroundColor: theme.palette.background.paper,
                    padding: `${paddingContent / 2}px ${paddingContent}px`,
                    flexShrink: 0,
                    position: 'relative',
                    zIndex: 5,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-around',
                        alignItems: 'center',
                        gap: 0.5,
                        maxWidth: '100%',
                        overflowX: 'auto',
                        paddingX: 1,
                    }}
                >
                    {items.map((item, index) => (
                        <Box
                            key={item.identifier || index}
                            data-testid={`navigation-item-${item.identifier}`}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                minWidth: 'fit-content',
                            }}
                        >
                            <IconButton
                                data-testid={`navigation-button-${item.identifier}`}
                                onClick={() => {
                                    if (item.onClick) {
                                        item.onClick(item.identifier || '');
                                        onTabChange(item.identifier || '');
                                    }
                                }}
                                sx={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '12px',
                                    color:
                                        activeTab === item.identifier
                                            ? 'primary.main'
                                            : 'text.secondary',
                                    backgroundColor:
                                        activeTab === item.identifier
                                            ? 'primary.50'
                                            : 'transparent',
                                    '&:hover': {
                                        backgroundColor:
                                            activeTab === item.identifier
                                                ? 'primary.100'
                                                : 'grey.100',
                                    },
                                }}
                            >
                                {item.icon && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            '& svg': {
                                                fontSize: '16px !important',
                                            },
                                        }}
                                    >
                                        <FaOptionIcon
                                            icon={item.icon.icon}
                                            size="sm"
                                            color={
                                                activeTab === item.identifier
                                                    ? 'primary.main'
                                                    : 'text.secondary'
                                            }
                                        />
                                    </Box>
                                )}
                            </IconButton>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: '10px',
                                    lineHeight: 1.2,
                                    marginTop: '2px',
                                    textAlign: 'center',
                                    color:
                                        activeTab === item.identifier
                                            ? 'primary.main'
                                            : 'text.secondary',
                                    maxWidth: '60px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {item.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        );
    };
